import React, { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    toggleAddModal,
    toggleNeedRefresh,
} from '../../store/organizersSlice';
import ApiService from '../../components/api_services/apiService.jsx';
import GusService from '../../components/api_services/gusService.js';
import { ReactComponent as AddSquareIcon } from '../../components/icons/add-icon.svg';
import { ReactComponent as CloseIcon } from '../../components/icons/close.svg';
import { ReactComponent as ArrowRightIcon } from '../../components/icons/arrow-right.svg';
import { ReactComponent as ArrowLeftIcon } from '../../components/icons/arrow-left.svg';
import './AddOrganizerModal.css';

function AddOrganizerModal() {
    const dispatch = useDispatch();
    const isAddModalOpen = useSelector((state) => state.organizers.addModalOpen);

    // Step management
    const [currentStep, setCurrentStep] = useState(1);
    const totalSteps = 2;

    // Form data
    const [formData, setFormData] = useState({
        companyName: '',
        nip: '',
        street: '',
        postalCode: '',
        city: '',
        email: '',
        password: '',
        description: '',
        facebookUrl: '',
        websiteUrl: '',
    });

    const [isSubmitDisabled, setIsSubmitDisabled] = useState(true);
    const [isLoadingGUS, setIsLoadingGUS] = useState(false);

    const cleanNip = formData.nip.replace(/[^0-9]/g, '');
    const isNipValid = cleanNip.length === 10;
    const isPasswordValid = /^(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z0-9]).{8,}$/.test(formData.password);

    // Validation
    useEffect(() => {
        const isValid = Boolean(
            formData.companyName &&
            isNipValid &&
            formData.street &&
            formData.postalCode &&
            formData.city &&
            formData.email &&
            isPasswordValid
        );
        setIsSubmitDisabled(!isValid);
    }, [formData, isNipValid, isPasswordValid]);

    // Input handlers
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        const nextValue = name === 'nip' ? value.replace(/[^0-9]/g, '') : value;
        setFormData((prev) => ({ ...prev, [name]: nextValue }));
    };

    const connectWithGUS = useCallback(async () => {
        if (!isNipValid) {
            alert('Podaj poprawny 10-cyfrowy numer NIP');
            return;
        }

        setIsLoadingGUS(true);
        try {
            const companyData = await GusService.fetchFirmaByNip(cleanNip);
            setFormData((prev) => ({
                ...prev,
                companyName: companyData.nazwa || prev.companyName,
                street: companyData.adres || prev.street,
                postalCode: companyData.kodPocztowy || prev.postalCode,
                city: companyData.miasto || prev.city,
            }));
        } catch (error) {
            console.error('GUS fetch failed:', error);
            alert(error.message || 'Nie udało się pobrać danych z GUS.');
        } finally {
            setIsLoadingGUS(false);
        }
    }, [cleanNip, isNipValid]);

    // Navigation
    const handleNextStep = () => {
        if (currentStep < totalSteps) {
            setCurrentStep(currentStep + 1);
        }
    };

    const handlePrevStep = () => {
        if (currentStep > 1) {
            setCurrentStep(currentStep - 1);
        }
    };

    const resetForm = () => {
        setFormData({
            companyName: '',
            nip: '',
            street: '',
            postalCode: '',
            city: '',
            email: '',
            password: '',
            description: '',
            facebookUrl: '',
            websiteUrl: '',
        });
        setCurrentStep(1);
    };

    const handleClose = () => {
        resetForm();
        if (isAddModalOpen) dispatch(toggleAddModal());
    };

    // Submit
    const handleSubmit = async (e) => {
        e.preventDefault();

        const payload = {
            email: formData.email,
            pass: formData.password,
            nip: cleanNip,
            companyName: formData.companyName,
            street: formData.street,
            city: formData.city,
            postalCode: formData.postalCode,
            contactEmail: formData.email,
            description: formData.description,
            socialLink: formData.facebookUrl,
            siteLink: formData.websiteUrl,
        };

        try {
            await ApiService.registerOrganiser(payload);
            dispatch(toggleNeedRefresh());
            handleClose();
        } catch (error) {
            console.error('Error saving organizer:', error);
        }
    };

    if (!isAddModalOpen) return null;

    return (
        <>
            {/* Backdrop */}
            <div className="add-organizer-backdrop" onClick={handleClose} />

            {/* Modal */}
            <div className="add-organizer-modal">
                <div className="add-organizer-layout">
                    {/* Header Section */}
                    <div className="add-organizer-header-section">
                        {/* Title Row */}
                        <div className="add-organizer-title-row">
                            <div className="add-organizer-title">
                                <AddSquareIcon className="add-organizer-title-icon" />
                                <span>Dodaj organizatora</span>
                            </div>
                            <button className="add-organizer-close-btn" onClick={handleClose}>
                                <CloseIcon />
                            </button>
                        </div>

                        {/* Stepper */}
                        <div className="add-organizer-stepper">
                            <div className={`stepper-item ${currentStep >= 1 ? 'active' : ''}`}>
                                <div className="stepper-number">1</div>
                                <span className="stepper-label">Dane podstawowe</span>
                            </div>
                            <div className="stepper-divider">------</div>
                            <div className={`stepper-item ${currentStep >= 2 ? 'active' : 'inactive'}`}>
                                <div className="stepper-number">2</div>
                                <span className="stepper-label">Opis i linki</span>
                            </div>
                        </div>
                    </div>

                    {/* Form Content */}
                    <div className="add-organizer-content">
                        {currentStep === 1 && (
                            <div className="add-organizer-step step-1">
                                <div className="form-section">
                                    {/* NIP */}
                                    <div className="form-row">
                                        <div className="form-field full-width">
                                            <label className="form-label">NIP</label>
                                            <div className="form-input-with-button">
                                                <div className="form-input-wrapper">
                                                    <input
                                                        type="text"
                                                        name="nip"
                                                        value={formData.nip}
                                                        onChange={handleInputChange}
                                                        placeholder="0000000000"
                                                        maxLength={10}
                                                        className="form-input"
                                                    />
                                                </div>
                                                <button
                                                    type="button"
                                                    className="gus-fetch-button"
                                                    onClick={connectWithGUS}
                                                    disabled={isLoadingGUS || !isNipValid}
                                                >
                                                    {isLoadingGUS ? 'Pobieranie...' : 'Pobierz dane z GUS'}
                                                </button>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Company Name */}
                                    <div className="form-row">
                                        <div className="form-field full-width">
                                            <label className="form-label">Nazwa firmy</label>
                                            <div className="form-input-wrapper">
                                                <input
                                                    type="text"
                                                    name="companyName"
                                                    value={formData.companyName}
                                                    onChange={handleInputChange}
                                                    className="form-input"
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    {/* Email */}
                                    <div className="form-row">
                                        <div className="form-field full-width">
                                            <label className="form-label">Kontakt</label>
                                            <div className="form-input-wrapper">
                                                <input
                                                    type="email"
                                                    name="email"
                                                    value={formData.email}
                                                    onChange={handleInputChange}
                                                    placeholder="adres e-mail"
                                                    className="form-input"
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    {/* Password */}
                                    <div className="form-row">
                                        <div className="form-field full-width">
                                            <label className="form-label">Hasło startowe</label>
                                            <div className="form-input-wrapper">
                                                <input
                                                    type="password"
                                                    name="password"
                                                    value={formData.password}
                                                    onChange={handleInputChange}
                                                    placeholder="Min. 8 znaków, wielka litera, cyfra, znak specjalny"
                                                    className="form-input"
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Address Section */}
                                <div className="form-section">
                                    <div className="section-header">
                                        <span className="section-title">Adres</span>
                                    </div>

                                    <div className="form-row">
                                        <div className="form-field full-width">
                                            <label className="form-label">Ulica</label>
                                            <div className="form-input-wrapper">
                                                <input
                                                    type="text"
                                                    name="street"
                                                    value={formData.street}
                                                    onChange={handleInputChange}
                                                    className="form-input"
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    <div className="form-row two-columns">
                                        <div className="form-field">
                                            <label className="form-label">Kod pocztowy</label>
                                            <div className="form-input-wrapper">
                                                <input
                                                    type="text"
                                                    name="postalCode"
                                                    value={formData.postalCode}
                                                    onChange={handleInputChange}
                                                    placeholder="00-000"
                                                    className="form-input"
                                                />
                                            </div>
                                        </div>
                                        <div className="form-field">
                                            <label className="form-label">Miejscowość</label>
                                            <div className="form-input-wrapper">
                                                <input
                                                    type="text"
                                                    name="city"
                                                    value={formData.city}
                                                    onChange={handleInputChange}
                                                    placeholder="Nazwa miasta"
                                                    className="form-input"
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {currentStep === 2 && (
                            <div className="add-organizer-step step-2">
                                {/* Description */}
                                <div className="form-section">
                                    <div className="form-row">
                                        <div className="form-field full-width">
                                            <label className="form-label">Opis działalności</label>
                                            <div className="form-textarea-wrapper">
                                                <textarea
                                                    name="description"
                                                    value={formData.description}
                                                    onChange={handleInputChange}
                                                    placeholder="Opisz działalność organizatora..."
                                                    className="form-textarea"
                                                    rows={5}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Links */}
                                <div className="form-section">
                                    <div className="form-row">
                                        <div className="form-field full-width">
                                            <label className="form-label">Link do Facebook</label>
                                            <div className="form-input-wrapper">
                                                <input
                                                    type="url"
                                                    name="facebookUrl"
                                                    value={formData.facebookUrl}
                                                    onChange={handleInputChange}
                                                    placeholder="https://"
                                                    className="form-input"
                                                />
                                            </div>
                                        </div>
                                    </div>
                                    <div className="form-row">
                                        <div className="form-field full-width">
                                            <label className="form-label">Strona WWW</label>
                                            <div className="form-input-wrapper">
                                                <input
                                                    type="url"
                                                    name="websiteUrl"
                                                    value={formData.websiteUrl}
                                                    onChange={handleInputChange}
                                                    placeholder="https://"
                                                    className="form-input"
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Footer */}
                    <div className="add-organizer-footer">
                        <div className="footer-actions">
                            <button
                                type="button"
                                className="footer-btn footer-btn-outline"
                                onClick={handleClose}
                            >
                                Anuluj
                            </button>

                            <div className="footer-nav-buttons">
                                {currentStep > 1 && (
                                    <button
                                        type="button"
                                        className="footer-btn footer-btn-outline"
                                        onClick={handlePrevStep}
                                    >
                                        <ArrowLeftIcon className="btn-icon" />
                                        Wstecz
                                    </button>
                                )}

                                {currentStep < totalSteps ? (
                                    <button
                                        type="button"
                                        className="footer-btn footer-btn-filled"
                                        onClick={handleNextStep}
                                    >
                                        Dalej
                                        <ArrowRightIcon className="btn-icon" />
                                    </button>
                                ) : (
                                    <button
                                        type="button"
                                        className="footer-btn footer-btn-filled"
                                        onClick={handleSubmit}
                                        disabled={isSubmitDisabled}
                                    >
                                        <AddSquareIcon className="btn-icon" />
                                        Dodaj organizatora
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}

export default AddOrganizerModal;
